import { Component } from '@theme/component';

/**
 * Accessible tabs used by the "Tabs with tiles" mobile drawer style.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLElement} [indicator]
 *
 * @extends Component<Refs>
 */
class MenuDrawerTabs extends Component {
  requiredRefs = ['tabs', 'panels'];

  #resizeObserver = new ResizeObserver(() => this.#moveIndicator());

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.#moveIndicator();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  get #selectedIndex() {
    return Math.max(
      0,
      this.refs.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );
  }

  /**
   * Selects a tab by index.
   * @param {number | string} index
   * @param {Event} [event]
   */
  select(index, event) {
    const selected = Number(index);

    this.refs.tabs.forEach((tab, i) => {
      const active = i === selected;
      tab.setAttribute('aria-selected', String(active));
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.refs.panels.forEach((panel, i) => {
      panel.hidden = i !== selected;
    });

    this.#moveIndicator();

    if (event instanceof KeyboardEvent) this.refs.tabs[selected]?.focus();
  }

  /**
   * Arrow-key navigation between tabs (WAI-ARIA tabs pattern).
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const count = this.refs.tabs.length;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = (this.#selectedIndex + 1) % count;
        break;
      case 'ArrowLeft':
        next = (this.#selectedIndex - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next, event);
  }

  #moveIndicator() {
    const { indicator, tabs } = this.refs;
    const tab = tabs[this.#selectedIndex];

    if (!indicator || !tab) return;

    indicator.style.setProperty('--indicator-left', `${tab.offsetLeft}px`);
    indicator.style.setProperty('--indicator-width', `${tab.offsetWidth}px`);
  }
}

if (!customElements.get('menu-drawer-tabs')) {
  customElements.define('menu-drawer-tabs', MenuDrawerTabs);
}
